import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon, Table, Button } from "semantic-ui-react";
import { Industry, Inspection, ChecklistNormReference, Source } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite } from "../../../../contexts";
import { useChecklistSiteIndustry } from "../useChecklistSiteIndustry";
import { NewChecklistButton } from "../NewChecklistButton";
import { NORM_COUNTRIES } from "../normCountries";
import "./ChecklistCatalogs.scss";

const industryCtrl   = new Industry();
const inspectionCtrl = new Inspection();
const normRefCtrl    = new ChecklistNormReference();
const sourceCtrl      = new Source();

export function ChecklistCatalogs({ onCreateTemplate, onGoTemplates }) {
  const { accessToken } = useAuth();
  const { site: activeSite } = useSite();
  const [siteIndustry, setSiteIndustry] = useChecklistSiteIndustry(activeSite);
  const [savingSiteIndustry, setSavingSiteIndustry] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [normRefs,      setNormRefs]      = useState([]);
  const [loadingNorms,  setLoadingNorms]  = useState(true);
  const [savingNorm,    setSavingNorm]    = useState(false);
  const [newNorm, setNewNorm] = useState({ template: "", country: NORM_COUNTRIES[0].value, norm: "" });
  const [sources, setSources] = useState([]);

  // Catálogo "Norma" de Requisitos Legales (modelo Source) — se trae solo
  // para copiar su texto al campo `norm` de acá, sin vínculo vivo ni
  // modificar ese módulo (lectura, ya viene filtrado por empresa/global).
  useEffect(() => {
    sourceCtrl.getSources(accessToken, true).then((res) => setSources(Array.isArray(res) ? res : [])).catch(() => setSources([]));
  }, [accessToken]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      industryCtrl.getIndustries(accessToken, true).catch(() => []),
      inspectionCtrl.getTemplates(accessToken, { active: true }).catch(() => []),
    ])
      .then(([inds, tpls]) => {
        setIndustries(Array.isArray(inds) ? inds : []);
        setTemplates(Array.isArray(tpls) ? tpls : []);
      })
      .finally(() => setLoading(false));
  }, [accessToken]);

  const loadNormRefs = () => {
    setLoadingNorms(true);
    normRefCtrl.getNormReferences(accessToken)
      .then((res) => setNormRefs(Array.isArray(res) ? res : []))
      .catch(() => setNormRefs([]))
      .finally(() => setLoadingNorms(false));
  };

  useEffect(loadNormRefs, [accessToken]);

  const handleAddNorm = async () => {
    if (!newNorm.template || !newNorm.country || !newNorm.norm.trim()) {
      toast.warning("Completá plantilla, país y norma.");
      return;
    }
    setSavingNorm(true);
    try {
      await normRefCtrl.createNormReference(accessToken, {
        template: newNorm.template,
        country:  newNorm.country,
        norm:     newNorm.norm.trim(),
      });
      toast.success("Norma de referencia agregada.");
      setNewNorm({ template: "", country: NORM_COUNTRIES[0].value, norm: "" });
      loadNormRefs();
    } catch (err) {
      toast.error(err?.msg || "Error al agregar la norma de referencia.");
    } finally {
      setSavingNorm(false);
    }
  };

  const handleChangeSiteIndustry = async (e) => {
    const industryId = e.target.value;
    if (!industryId || !activeSite || activeSite === "0") return;
    setSavingSiteIndustry(true);
    try {
      const profile = await inspectionCtrl.setSiteIndustryProfile(accessToken, activeSite, industryId);
      setSiteIndustry(profile?.industry || industries.find((i) => i._id === industryId) || null);
      toast.success("Industria del sitio actualizada.");
    } catch {
      toast.error("Error al guardar la industria del sitio.");
    } finally {
      setSavingSiteIndustry(false);
    }
  };

  const handleDeleteNorm = async (id) => {
    try {
      await normRefCtrl.deleteNormReference(accessToken, id);
      setNormRefs((prev) => prev.filter((n) => n._id !== id));
    } catch {
      toast.error("Error al eliminar la norma de referencia.");
    }
  };

  const itemCategories = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => (t.items || []).forEach((i) => { if (i.category) set.add(i.category); }));
    return [...set];
  }, [templates]);

  const seedCounts = { templates: templates.length };

  const rows = [
    {
      key: "industries",
      label: "Industrias / Sectores",
      value: loading ? "Cargando…" : (industries.map((i) => i.name).join(", ") || "Sin datos"),
      link: { to: "/admin/industries", text: "Administrar industrias" },
    },
    {
      key: "categories",
      label: "Categorías de ítem",
      value: loading ? "Cargando…" : (itemCategories.join(", ") || "Sin datos"),
      note: "No tienen pantalla propia — se definen libremente al cargar ítems en una plantilla.",
    },
    {
      key: "templates",
      label: "Plantillas de checklist",
      value: `Semilla por industria + propias del usuario${loading ? "" : ` · ${seedCounts.templates} activas`}`,
    },
    {
      key: "result",
      label: "Resultado del ítem",
      value: "Sí (conforme) · No (hallazgo) · N/A",
    },
  ];

  return (
    <div className="ccat">
      {/* Título — pill blanco standalone, mismo patrón que el resto del
          módulo (chl-page__banner/chl-section-banner), ya no una card gris
          envolviendo todo. */}
      <div className="ccat__title-bar">
        Catálogo de módulo
      </div>

      {/* Cada fila es su propio rectángulo blanco (label | valor), con el
          link/acción de la fila flotando arriba a la derecha — mismo
          criterio "filas como pills" que el resto del módulo. */}
      <div className="ccat__rows">
        <div className="ccat__row ccat__row--header">
          <span>Catálogo</span>
          <span>Valor semilla</span>
        </div>
        {rows.map((r) => (
          <div className={`ccat__row${r.link || r.action ? " ccat__row--has-pill" : ""}`} key={r.key}>
            <span className="ccat__row-label">{r.label}</span>
            <div className="ccat__row-value">
              {r.value}
              {r.note && <div className="ccat__row-note">{r.note}</div>}
            </div>
            {r.link && (
              <Link className="ccat__row-pill" to={r.link.to}>{r.link.text}</Link>
            )}
            {r.action && (
              r.action.onClick === onCreateTemplate ? (
                <NewChecklistButton className="ccat__row-pill" onSelect={onCreateTemplate}>
                  {r.action.text}
                </NewChecklistButton>
              ) : (
                <button type="button" className="ccat__row-pill" onClick={r.action.onClick}>
                  {r.action.text}
                </button>
              )
            )}
          </div>
        ))}
        {!onCreateTemplate && onGoTemplates && (
          <button type="button" className="ccat__row ccat__row--link-only" onClick={onGoTemplates}>
            Ir a Explorar checklists
          </button>
        )}
      </div>

      <div className="ccat__card">
        <h3 className="ccat__card-title">
          <Icon name="building outline" />
          Industria del sitio activo
        </h3>
        <p className="ccat__card-sub">
          Define a qué industria pertenece el sitio seleccionado en el encabezado — se usa para filtrar
          qué checklists le corresponden en Explorar y para el campo INDUSTRIA del informe PDF. No es la
          industria de la plantilla (eso se define al crear/editar cada checklist).
        </p>
        {!activeSite || activeSite === "0" ? (
          <div className="ccat__row-note">Seleccioná un sitio desde el encabezado para asignarle industria.</div>
        ) : (
          <div className="ccat__norm-form">
            <select
              className="ccat__norm-select"
              value={siteIndustry?._id || ""}
              onChange={handleChangeSiteIndustry}
              disabled={savingSiteIndustry}
            >
              <option value="" disabled>Sin asignar</option>
              {industries.map((ind) => (
                <option key={ind._id} value={ind._id}>{ind.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="ccat__card">
        <h3 className="ccat__card-title">
          <Icon name="balance scale" />
          Normas de referencia (plantilla × país)
        </h3>
        <p className="ccat__card-sub">
          Catálogo propio de este módulo — no depende de Requisitos Legales (ese módulo no tiene
          estructura industria × país). Va por plantilla (no por industria) porque distintas plantillas
          de una misma industria pueden referenciar normas distintas. Se usa para mostrar la norma de
          referencia en la pantalla de Ejecutar.
        </p>

        <div className="ccat__norm-form">
          <select
            className="ccat__norm-select"
            value={newNorm.template}
            onChange={(e) => setNewNorm((p) => ({ ...p, template: e.target.value }))}
          >
            <option value="">Plantilla...</option>
            {templates.map((tpl) => (
              <option key={tpl._id} value={tpl._id}>
                {tpl.name}{tpl.industry?.name ? ` (${tpl.industry.name})` : ""}
              </option>
            ))}
          </select>
          <select
            className="ccat__norm-select"
            value={newNorm.country}
            onChange={(e) => setNewNorm((p) => ({ ...p, country: e.target.value }))}
          >
            {NORM_COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>{c.text}</option>
            ))}
          </select>
          <input
            type="text"
            className="ccat__norm-input"
            placeholder="Norma (ej. NR-12 — máquinas e equipamentos)"
            value={newNorm.norm}
            onChange={(e) => setNewNorm((p) => ({ ...p, norm: e.target.value }))}
          />
          <select
            className="ccat__norm-select"
            value=""
            onChange={(e) => {
              const src = sources.find((s) => s._id === e.target.value);
              if (src) setNewNorm((p) => ({ ...p, norm: src.name }));
            }}
          >
            <option value="">Copiar desde Requisitos Legales...</option>
            {sources.map((s) => (
              <option key={s._id} value={s._id}>{s.code} — {s.name}</option>
            ))}
          </select>
          <Button primary size="small" loading={savingNorm} disabled={savingNorm} onClick={handleAddNorm}>
            + Agregar
          </Button>
        </div>
        <p className="ccat__row-note">
          El selector "Copiar desde Requisitos Legales" trae las normas ya cargadas en ese módulo (catálogo "Norma")
          y copia el texto al campo — queda como texto propio de Checklist, editable, sin vínculo con el original.
        </p>

        {loadingNorms ? (
          <div className="ccat__row-note">Cargando…</div>
        ) : normRefs.length === 0 ? (
          <div className="ccat__row-note">Todavía no hay normas cargadas.</div>
        ) : (
          <Table className="ccat__table" basic="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Plantilla</Table.HeaderCell>
                <Table.HeaderCell>País</Table.HeaderCell>
                <Table.HeaderCell>Norma</Table.HeaderCell>
                <Table.HeaderCell textAlign="right"></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {normRefs.map((n) => (
                <Table.Row key={n._id}>
                  <Table.Cell>
                    {n.template?.name || "-"}
                    {n.template?.industry?.name && (
                      <span className="ccat__row-note" style={{ display: "block" }}>{n.template.industry.name}</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>{NORM_COUNTRIES.find((c) => c.value === n.country)?.text || n.country}</Table.Cell>
                  <Table.Cell>{n.norm}</Table.Cell>
                  <Table.Cell textAlign="right">
                    <button type="button" className="ccat__norm-delete" onClick={() => handleDeleteNorm(n._id)}>
                      <Icon name="trash" />
                    </button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
}
